import { t } from "@/i18n-js/instance";
import { NewEventButton } from "@/react/components/Events/NewEventButton";
import { usePunditUserContext } from "@circle-react/contexts";
import { isCommunityAdmin } from "@circle-react/helpers/communityMemberHelpers";
import { useUpdateCommunitySetting } from "@circle-react/hooks/UseCommunitySetting";
import { ViewSwitcher } from "@circle-react-shared/ViewSwitcher";
import type { ViewSwitcherLayoutTypes } from "@circle-react-shared/ViewSwitcher";
import { useToast } from "@circle-react-shared/uikit/ToastV2";
import { Loader } from "@circle-react-uikit/Loader";
import { PublicSettingButton } from "./PublicSettingButton";
import { SettingsAction } from "./SettingsAction";
import { EVENT_LAYOUT_OPTIONS, EVENT_VIEW_DISPLAY_VALUES } from "./constants";

interface RightOptionsProps {
  activeView: ViewSwitcherLayoutTypes;
  communityData: any;
  isPublicEventsPage: boolean;
  setActiveView: (value: ViewSwitcherLayoutTypes) => void;
}

export const RightOptions = ({
  activeView,
  communityData,
  isPublicEventsPage,
  setActiveView,
}: RightOptionsProps) => {
  const { currentCommunityMember, currentCommunitySettings } =
    usePunditUserContext();
  const isMemberPublicEventsViewEnabled = Boolean(
    currentCommunitySettings?.member_public_events_view_enabled,
  );
  const shouldAllowMembersToChangeView = Boolean(
    currentCommunitySettings?.allow_member_public_events_view,
  );
  const updateCommunitySettingMutation = useUpdateCommunitySetting();
  const toast = useToast();

  const onLayoutChange = (view: any) => {
    if (isMemberPublicEventsViewEnabled) {
      setActiveView(view);
      return;
    }

    updateCommunitySettingMutation
      .mutateAsync({
        data: communityData,
        updates: {
          public_events_view: view,
        },
      })
      .then(() => {
        toast.success(
          t("all_events.view_changed_success", {
            layout: EVENT_VIEW_DISPLAY_VALUES[view],
          }),
        );
      })
      .catch(err => {
        console.error(err);
        toast.error(t("all_events.view_changed_failure"));
      });
  };

  const shouldDisplayMemberEventsView =
    isMemberPublicEventsViewEnabled && shouldAllowMembersToChangeView;

  if (!isCommunityAdmin(currentCommunityMember)) {
    if (shouldDisplayMemberEventsView) {
      return (
        <div className="flex items-center gap-4">
          <ViewSwitcher
            value={activeView}
            onChange={onLayoutChange}
            options={EVENT_LAYOUT_OPTIONS}
          />
        </div>
      );
    }
    return null;
  }

  return (
    <div className="flex items-center gap-4">
      {updateCommunitySettingMutation.isLoading ? (
        <Loader />
      ) : (
        <ViewSwitcher
          value={activeView}
          onChange={onLayoutChange}
          options={EVENT_LAYOUT_OPTIONS}
        />
      )}
      <div className="hidden sm:flex">
        <PublicSettingButton
          communityData={communityData}
          isPublicEventsPage={isPublicEventsPage}
        />
      </div>
      <NewEventButton />
      {isMemberPublicEventsViewEnabled && <SettingsAction />}
    </div>
  );
};
